import React, { ReactNode, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons'
import { Input } from 'antd'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import ButtonProps from 'design-system/button/src/resources/ButtonProps.interface'
import styles from './ResetModal.module.scss'

interface IResetModal {
  show: boolean
  title?: string
  securityWord: string
  text: string
  textConfirmation: string
  acceptButtonText: ReactNode
  onAccept?: any
  onClose: any
  size?: 'sm' | 'lg' | 'xl'
  backdropClassName?: string
  acceptButtonProps?: Partial<ButtonProps>
  cancelButtonProps?: Partial<ButtonProps>
  loading?: boolean
}

export const ResetModal: React.FC<IResetModal> = ({
  show,
  title = '',
  securityWord,
  text,
  textConfirmation,
  acceptButtonText,
  onAccept,
  onClose,
  size,
  backdropClassName,
  acceptButtonProps,
  cancelButtonProps,
  loading,
}) => {
  const { t } = useTranslation()
  const [wordInput, setWordInput] = useState('')
  const prefix = 'administration.monitor.resetSelected.modal'

  const checkSecurityWord = () => {
    return wordInput === securityWord ? false : true
  }

  const handleText = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setWordInput(e.target.value)
  }

  const handleSend = () => {
    onAccept()
    setWordInput('')
  }

  return (
    <Modal
      show={show}
      size={size || 'lg'}
      centered
      className={styles.modalContainer}
      onHide={onClose}
      backdropClassName={backdropClassName}
    >
      <Modal.Header
        translate="yes"
        closeButton
        className={`${styles.warningModalHeader} d-flex align-items-center`}
      >
        <div className={styles.header}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className={styles.icon}
          />
          <h1 className={styles.title}>{title}</h1>
        </div>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">
        <div className={styles.textWarning}>{text}</div>
        <div className={styles.textReset}>{textConfirmation}</div>
        <span className={styles.securityWord}>
          {t(`${prefix}.securityText`)}
        </span>
        <div>
          <Input
            className={styles.input}
            value={wordInput}
            onChange={handleText}
            placeholder={t(`${prefix}.placeholder`)}
          />
        </div>
      </Modal.Body>
      <div className="px-4 d-flex justify-content-center">
        <div className="dropdown-divider m-0 w-100 " />
      </div>
      <Modal.Footer className="border-0">
        <Button
          size={ButtonSizes.Small}
          variant={ButtonVariant.Secondary}
          className={`my-0 ${styles.customDefaultButton}`}
          onClick={onClose}
          disabled={loading}
          {...cancelButtonProps}
        >
          {t(`${prefix}.cancel`)}
        </Button>
        <Button
          size={ButtonSizes.Small}
          variant={ButtonVariant.Danger}
          className={`my-0 ${styles.customDangerButton}`}
          onClick={handleSend}
          disabled={checkSecurityWord()}
          {...acceptButtonProps}
        >
          {loading && (
            <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
          )}
          {acceptButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
